import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { CollaborationEmail } from '@/types/collaborations';

export const useCollaborationEmails = (campaignId: string | null) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: emails, isLoading } = useQuery({
    queryKey: ['collaboration-emails', campaignId],
    queryFn: async () => {
      if (!campaignId) return [];
      
      const { data, error } = await supabase
        .from('os_collabs_emails')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('sequence_order', { ascending: true });
      
      if (error) throw error;
      return data as CollaborationEmail[];
    },
    enabled: !!campaignId
  });

  const createMutation = useMutation({
    mutationFn: async (emailData: Partial<CollaborationEmail>) => {
      if (!campaignId) throw new Error('Campaign ID required');

      const { data, error } = await supabase
        .from('os_collabs_emails')
        .insert([{
          campaign_id: campaignId,
          subject: emailData.subject || '',
          content: emailData.content || '',
          sequence_order: emailData.sequence_order || 1,
          delay_days: emailData.delay_days || 0,
          delay_hours: emailData.delay_hours || 0,
          send_conditions: emailData.send_conditions ?? null,
          stop_conditions: (emailData as any).stop_conditions ?? null,
          prompt_id: emailData.prompt_id,
        } as any])
        .select()
        .single();
      
      if (error) throw error;
      return data as CollaborationEmail;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaboration-emails', campaignId] });
      toast({ title: "Success", description: "Email created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CollaborationEmail> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_collabs_emails')
        .update(updates as any)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as CollaborationEmail;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaboration-emails', campaignId] });
      toast({ title: "Success", description: "Email updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_collabs_emails')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaboration-emails', campaignId] });
      toast({ title: "Success", description: "Email deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  return {
    emails: emails || [],
    isLoading,
    createEmail: createMutation.mutate,
    updateEmail: updateMutation.mutate,
    deleteEmail: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
